import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngxs/store';
import { CartAction } from '../_cart/cart.actions';
import Remove = CartAction.Remove;
import IncrementQty = CartAction.IncrementQty;
import DecrementQty = CartAction.DecrementQty;
import ClearTheCart = CartAction.ClearTheCart;
import SetCartId = CartAction.SetId;
import { getPrice, Offer } from '../_models/offer';
import UpdateShippingOffer = CartAction.UpdateShippingOffer;
import { Observable, of, EMPTY } from 'rxjs';
import { ProductModel } from '../_cart/product-model';
import UpdateOrderId = CartAction.UpdateOrderId;
import { CartStateModel } from '../_cart/cart.state';
import { User } from '../_models/user';
import { OrderService } from './order.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CartItem } from '../_models/cart-item';
import { AppConstants } from '../_constants/constants';
import { map, mergeMap } from 'rxjs/operators';
import { StoreService } from './store.service';
import ToggleSelect = CartAction.ToggleSelect;
import SelectAll = CartAction.SelectAll;
import SelectOnly = CartAction.SelectOnly;

@Injectable({
    providedIn: 'root'
})
export class CartService {
    user: User;
    cartSize = 0;

    constructor(private store: Store, private orderService: OrderService,
        private httpClient: HttpClient, private storeService: StoreService,
        @Inject(PLATFORM_ID) private platformId: Object) {
        // Only access localStorage in browser environment
        if (isPlatformBrowser(this.platformId)) {
            const currentUserJson = localStorage.getItem('currentUser');
            this.user = currentUserJson ? JSON.parse(currentUserJson) : null;
        }
        this.storeService.findAll().subscribe(response => {
            this.cartSize = response.length
        });
    }

    loadItems(userId: number): Observable<CartItem[]> {
        return of ([])
        // return this.httpClient.get<CartItem[]>(environment.apiUrl + '/user/' + userId + '/cart');
    }

    addOrUpdateItemIntoCart(userId: number, offerId: number, price: number, shipTo: any, shippingOfferId: number, qty: number): void {
    }


    private buildCartItem(offer: Offer, qty: number): CartItem {
        return {
            itemId: ++this.cartSize,
            shippingOfferPrice: 0,
            shippingOfferId: 0,
            price: getPrice(offer),
            qty: qty,
            currency: offer.currency,
            picture: this.getProductImage(offer),
            measurementUnit: offer.measurementunit.description,
            productName: offer.product.name,
            offerId: offer.id,
            shipTo: null,
            shippingOffers: offer.shippingOffers,
            localPickup: !!offer.localPickup
        }
    }

    addToLocalCart(offer: Offer, qty = 1): Observable<any> {
        this.storeService.retrieveCart()
            .subscribe(cart => {
                if (!cart.cartId) { // if the current cart does not have an id, request for one and register it
                    this.getAgentCartId()
                        .subscribe(id => {
                            this.store.dispatch(new SetCartId(id));
                        });
                }
            })

        const cartItem = this.buildCartItem(offer, qty);
        return this.addItemToStore(cartItem, qty);
    }

    addItemToCart(userId: number, offerId: number, price: number, shipTo: any, shippingOfferId: number, qty: number): Observable<CartItem> {
        return this.httpClient.post<CartItem>(environment.apiUrl + '/user/' + userId + '/cart', {
            offerId: offerId,
            price: price,
            qty: qty,
            shipTo: shipTo,
            shippingOfferId: shippingOfferId
        });
    }

    updateItemToCart(userId: number, offerId: number, price: number, shipTo: any, shippingOfferId: number, qty: number, itemId: number): Observable<any> {
        return this.httpClient.put(environment.apiUrl + '/user/' + userId + '/cart/' + itemId, {
            offerId: offerId,
            price: price,
            qty: qty,
            shipTo: shipTo,
            itemId: itemId,
            shippingOfferId: shippingOfferId
        });
    }

    removeItemFromCart(userId: number, itemId: number): Observable<any> {
        return this.httpClient.delete(environment.apiUrl + '/user/' + userId + '/cart/' + itemId);
    }

    removeAllItemsFromCart(userId: number): Observable<any> {
        return this.httpClient.delete(environment.apiUrl + '/user/' + userId + '/cart');
    }

    removeAgentCartId() {
        localStorage.removeItem(AppConstants.USER_AGENT_TOKEN);
    }

    getAgentCartId(): Observable<string> {
        const agentToken = localStorage.getItem(AppConstants.USER_AGENT_TOKEN);
        if (agentToken) {
            return of(agentToken);
        } else { // if there is no user token, then request for one and return it
            return this.httpClient.get<any>(environment.apiUrl + '/common/get_cart_identifier')
                .pipe(map(c => {
                    localStorage.setItem(AppConstants.USER_AGENT_TOKEN, c.code);
                    return c.code;
                }));
        }
    }



    getProducts(): Observable<ProductModel[]> {
        return this.store.select((state => state.cart.products));
    }

    getOrderId(): Observable<number> {
        return this.store.select((state => state.cart.orderId));
    }

    getCart(): Observable<CartStateModel> {
        return this.store.select((state => state.cart));
    }

    getClientCartId(): Observable<string> {
        return this.store.select((state => state.cart.cartId));
    }

    updateOrderId(orderId: number) {
        return this.store.dispatch(new UpdateOrderId(orderId));
    }

    addProduct(offer: Offer) {
        return this.store.dispatch(new IncrementQty(
            offer.id,
            {
                lineId: 0,
                id: offer.id,
                name: offer.product.name,
                qty: 1,
                price: getPrice(offer),
                slug: offer.product.slug,
                measurementUnit: offer.measurementunit.description,
                currency: offer.currency,
                imagePath: this.getProductImage(offer),
                shippingOffers: offer.shippingOffers,
                shippingOfferId: 0,
                selected: true,
                localPickup: !!offer.localPickup
            })
        );
    }


    public addItemToStore(cartItem: CartItem, qty = 0) {
        return this.store.dispatch(new IncrementQty(
            cartItem.offerId, {
            lineId: cartItem.itemId,
            id: cartItem.offerId,
            name: cartItem.productName,
            qty: cartItem.qty,
            price: cartItem.price,
            measurementUnit: cartItem.measurementUnit,
            slug: cartItem.productName,
            currency: cartItem.currency,
            imagePath: cartItem.picture,
            shippingOffers: cartItem.shippingOffers,
            shippingOfferId: cartItem.shippingOfferId,
            selected: true,
            localPickup: cartItem.localPickup
        }, qty));
    }

incrementQty(offer: Offer) {
        const item = this.buildCartItem(offer, 1);
        return this.store.dispatch(new IncrementQty(offer.id, item));
    }

    incrementQtyByProduct(product: ProductModel): Observable<void> {
        return this.store.dispatch(new IncrementQty(product.id, {
            lineId: product.lineId,
            id: product.id,
            name: product.name,
            qty: product.qty,
            price: product.price,
            measurementUnit: product.measurementUnit,
            slug: product.slug,
            currency: product.currency,
            imagePath: product.imagePath,
            shippingOffers: product.shippingOffers,
            shippingOfferId: product.shippingOfferId,
            selected: product.selected,
            localPickup: product.localPickup
        }));
    }

    decrementQty(id: number): Observable<void> {
        return this.store.dispatch(new DecrementQty(id));
    }

    removeProduct(id: number): void {
        this.store.dispatch(new Remove(id)).subscribe();
    }

    clearCart() {
        return this.store.dispatch(new ClearTheCart());
    }

    changeShippingOffer(id: number, shippingOfferId: number) {
        return this.store.dispatch(new UpdateShippingOffer(id, shippingOfferId));
    }

    getProduct(cart: CartStateModel, offerId: number) {
        let productFound = null;
        cart.products.forEach((product) => {
            if (offerId === product.id) {
                productFound = product;
            }
        });
        return productFound;
    }

    updateOrder(observable: Observable<any>, user: User, offer: Offer) {
        return observable.pipe(mergeMap((res: any) => {
            if (user !== null && res.cart.orderId !== undefined && res.cart.orderId !== 0) {
                return this.orderService.addOrUpdateLineOffer(user.id,
                    res.cart.orderId, offer.id, this.getProduct(res.cart, offer.id));
} else {
                return EMPTY;
            }
        }));
    }

    getImageSrc(imagePath: string) {
        if (typeof imagePath !== 'string' || imagePath === '' || !imagePath.includes('media')) {
            return '/assets/images/default.jpg';
        }

        const imageUrl = imagePath.startsWith('http')
            ? imagePath
            : environment.image_cdn_url + '/' + imagePath;

        return imageUrl.includes('?')
            ? imageUrl
            : imageUrl + '?func=fit&trim=0&width=357&height=357';
    }

    getProductImage(offer: Offer) {
        const imagePath = offer.defaultPicture
            || offer.mediaList?.[0]?.path
            || offer.product?.picture;

        return this.getImageSrc(imagePath);
    }

    toggleSelect(id: number) {
        return this.store.dispatch(new ToggleSelect(id));
    }

    selectAll(selected: boolean) {
        return this.store.dispatch(new SelectAll(selected));
    }

    selectOnly(id: number) {
        return this.store.dispatch(new SelectOnly(id));
    }

}
